import { useState } from "react";
import { Button } from "@mui/material";
import { FaPlusCircle } from "react-icons/fa";
import AccountModuleEntryCard from "./AccountModuleEntryCard";
import EditOtherEntryFiguresModal from "./EditOtherEntryFiguresModal";
import OtherEntryDocuments from "./OtherEntryDocuments";
import { AccountEmptyState, AccountPanelHeader, money } from "./accountEditorUi";
import { isAccountRowCompleted } from "../accountUtils";
import { isExternalModuleRow, isOtherEntryRow } from "../modulePaymentsRowUtils";
import { canAdjustAgencyFinancials } from "utils/permissionUtils";

/** A row's module is the label the server sends, but rows seeded locally have carried it as an
 * Autocomplete option object — anything that is not a known module name lands in this panel, so it
 * is read as text rather than assumed to be a string. */
const moduleLabel = (module) => (typeof module === "string" ? module.trim() : "");

const AccountOthersPanel = ({
  rows = [],
  onAddEntry,
  onChangeField,
  onApplyFigures,
  onOpenPayments,
  onOpenNotes,
  onOpenPendingVerifications,
  canReadVerifications = false,
  onDelete,
}) => {
  // Commission and bonus on a custom entry are the agency's money, set and claimed here in Direct
  // Account by whoever holds the keys for it — the server blanks them for everyone else.
  const showAgencyEarnings = canAdjustAgencyFinancials();
  const [editRow, setEditRow] = useState(null);

  const applyFigures = (fields) => {
    if (!editRow) return;
    const next = {
      agreedAmount: fields.agreedAmount,
      cost: fields.cost,
      discount: fields.discount,
      ...(showAgencyEarnings
        ? {
            commission: fields.commission,
            bonus: fields.bonus,
            isCommissionClaimed: fields.isCommissionClaimed,
            isBonusClaimed: fields.isBonusClaimed,
          }
        : {}),
    };
    onApplyFigures?.(editRow, next);
  };

  return (
    <div className="space-y-4">
      <AccountPanelHeader
        title="Other entries"
        description="Custom charges that are not synced from another module. Set agreed amount and cost, then record payments."
        count={rows.length}
        action={
          <Button variant="outlined" startIcon={<FaPlusCircle />} onClick={onAddEntry}>
            Add entry
          </Button>
        }
      />

      {rows.length === 0 ? (
        <AccountEmptyState
          title="No custom entries yet"
          description="Use this for one-off fees. Commission and bonus can be included in profit when they are received."
          actionLabel="Add entry"
          onAction={onAddEntry}
          actionIcon={<FaPlusCircle />}
        />
      ) : (
        <div className="space-y-3">
          {rows.map((row, i) => {
            const entryNumber = i + 1;
            const completed = isAccountRowCompleted(row);
            // A custom entry is linked to itself now; only a row owned by another module's
            // screen is read-only here.
            const linked = isExternalModuleRow(row);
            const pending = canReadVerifications && row.hasPendingModulePayment ? 1 : 0;
            // Payments run through the module verification flow, which is keyed on the row's
            // self-link to the Others module. That link is written server-side on save, so an
            // entry that has not been saved yet has nowhere to send a payment.
            const paymentsReady = Boolean(row.id) && (linked || isOtherEntryRow(row));
            const named = Boolean(moduleLabel(row.module));
            const bonus = Number(row.bonus) || 0;
            const discount = Number(row.discount) || 0;

            return (
              <AccountModuleEntryCard
                key={row.uuid}
                index={entryNumber}
                title={moduleLabel(row.module)}
                titlePlaceholder={`Entry ${entryNumber} — e.g. Document translation`}
                onTitleChange={
                  linked ? undefined : (value) => onChangeField?.(row, "module", value)
                }
                subtitle={linked ? "Linked from another module" : "Custom charge"}
                meta={[
                  ...(discount > 0 ? [{ label: "Discount", value: money(discount) }] : []),
                  ...(showAgencyEarnings && bonus > 0
                    ? [{ label: "Bonus", value: money(bonus) }]
                    : []),
                ]}
                agreed={row.agreedAmount}
                paid={row.paidAmount}
                due={row.dueAmount}
                cost={row.cost}
                commission={row.commission}
                showCommission={showAgencyEarnings}
                profitLoss={row.profitLoss}
                completed={completed}
                pendingCount={pending}
                onPendingClick={
                  pending && onOpenPendingVerifications
                    ? () => onOpenPendingVerifications(row)
                    : undefined
                }
                onOpenPayments={() => onOpenPayments?.(row)}
                paymentsVariant="contained"
                paymentsDisabled={!paymentsReady}
                paymentsTooltip={
                  paymentsReady
                    ? "Record a payment for verification"
                    : named
                    ? "Save the account first: a custom entry's payments go through account verification"
                    : "Name this entry, then save: a custom entry's payments go through account verification"
                }
                onOpenCharges={linked ? undefined : () => setEditRow(row)}
                figuresOnPaid={!linked}
                onOpenNotes={() => onOpenNotes?.(row)}
                onDelete={() => onDelete?.(row)}
                deleteDisabled={linked}
              >
                {/* {!linked && <OtherEntryDocuments cadId={row.id} />} */}
              </AccountModuleEntryCard>
            );
          })}
        </div>
      )}

      <EditOtherEntryFiguresModal
        open={Boolean(editRow)}
        row={editRow}
        showAgencyEarnings={showAgencyEarnings}
        onClose={() => setEditRow(null)}
        onSaved={applyFigures}
      />
    </div>
  );
};

export default AccountOthersPanel;
